/**
 * Whether a workflow's launch surface should render the Epic sign-in toggle
 * (and send `use_sp_login`). SP mode exists in `mortgagee_update_v1_2`
 * (per-USER dedicated SP) and in every POOLED workflow below; every other
 * workflow — including v1 `carrier_mortgagee_update` — must neither show the
 * toggle nor send `use_sp_login`.
 *
 * Single source of truth imported by BOTH front-ends (ui/CLAUDE.md parity rule):
 *   ora:     ~/common/workflows/shouldShowSpToggle
 *   add-in:  @product-app/common/workflows/shouldShowSpToggle
 *
 * Gated on the exact workflow id (not a schema field) — SP mode is a per-
 * workflow backend capability, so id membership is the precise, intentional
 * gate. The two generations use DIFFERENT credential stores and therefore
 * different readiness checks; see `usesSpPool`.
 *
 * features/3-plans/mortgagee-v1-sp-lock.md.
 */

export interface SpToggleCatalogEntryLike {
  id?: string | null;
}

/** v1.2's dedicated (per-USER) Service-Principal mode. */
export const SP_TOGGLE_WORKFLOW_ID = "mortgagee_update_v1_2";

/** The first pooled workflow — kept for existing imports. */
export const SP_POOL_WORKFLOW_ID = "carrier_mortgagee_update_v2";

/**
 * Every workflow that leases a slot from the SHARED pool (epic_session_locks +
 * epic-sp-NN-credentials). Same radio as v1.2, different backend; the payload
 * key (`use_sp_login`) is the same, which is why one toggle serves all.
 * The Epic test tools joined 2026-10-06: Reset v2 already sent the picker
 * through its schema checkbox, Smoke v2 had no pool support; both now get the
 * standard toggle plus the shared-pool readiness gate.
 */
export const SP_POOL_WORKFLOW_IDS: readonly string[] = [
  SP_POOL_WORKFLOW_ID,
  "epic_reset_test_policy_v2",
  "epic_smoke_test_v2",
];

/** Every workflow whose launch surface renders the Epic sign-in radio. */
export const SP_TOGGLE_WORKFLOW_IDS: readonly string[] = [
  SP_TOGGLE_WORKFLOW_ID,
  ...SP_POOL_WORKFLOW_IDS,
];

export function shouldShowSpToggle(
  catalogEntry: SpToggleCatalogEntryLike | null | undefined,
): boolean {
  const id = catalogEntry?.id;
  return typeof id === "string" && SP_TOGGLE_WORKFLOW_IDS.includes(id);
}

/**
 * Which readiness check gates a launch in SP mode — they are NOT interchangeable.
 *
 * v1.2 asks "are MY two dedicated-SP credentials saved?" (a per-user credential
 * status read). Pooled workflows ask "does the shared pool contain at least one
 * slot with credentials?" — a per-user check would pass for an operator who has
 * personal SP creds while the pool is empty, and the run would still dead-end on
 * `sp_credentials_missing`.
 */
export function usesSpPool(
  catalogEntry: SpToggleCatalogEntryLike | null | undefined,
): boolean {
  const id = catalogEntry?.id;
  return typeof id === "string" && SP_POOL_WORKFLOW_IDS.includes(id);
}
